/**
 * Voice Character Selector Component
 *
 * Displays voice characters in Male/Female tabs
 * Uses Inworld TTS (73 voices) with on-demand preview audio playback.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceCharacter, VoiceGender } from '../../types/voice-characters';
import voiceCharactersService from '../../services/voiceCharactersService';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import './VoiceCharacterSelector.css';

interface VoiceCharacterSelectorProps {
  selectedCharacterId: string | null;
  onSelectCharacter: (characterId: string) => void;
  speaker?: string; // For multi-speaker dialogues
  provider?: string; // TTS provider ('gemini' | 'inworld'), defaults to 'gemini'
}

const VoiceCharacterSelector: React.FC<VoiceCharacterSelectorProps> = ({
  selectedCharacterId,
  onSelectCharacter,
  speaker,
  provider = 'inworld',
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [activeGender, setActiveGender] = useState<VoiceGender>('Male');

  const GENDER_TABS: { id: VoiceGender; label: string; icon: string }[] = [
    { id: 'Male', label: t('creator:workflow.voiceCharacter.maleVoices'), icon: '👨' },
    { id: 'Female', label: t('creator:workflow.voiceCharacter.femaleVoices'), icon: '👩' },
  ];
  const [characters, setCharacters] = useState<{
    male: VoiceCharacter[];
    female: VoiceCharacter[];
  }>({
    male: [],
    female: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingCharacterId, setPlayingCharacterId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Fetch voice characters on mount and when provider changes
  useEffect(() => {
    const fetchCharacters = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await voiceCharactersService.getAllCharacters(provider);

        if (response.success && response.data) {
          setCharacters(response.data);
        } else {
          setError(response.error || 'Failed to load voice characters');
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCharacters();
  }, [provider]);

  // Play static preview audio directly (no API call)
  const handlePlayPreview = useCallback((character: VoiceCharacter) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    if (playingCharacterId === character.id) {
      setPlayingCharacterId(null);
      return;
    }

    if (!character.previewUrl) {
      console.error('No preview URL for', character.id);
      return;
    }

    const audioUrl = resolveMediaUrl(character.previewUrl);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.play().catch((err) => {
      console.error('Failed to play audio preview:', err);
      setPlayingCharacterId(null);
    });

    audio.onended = () => {
      setPlayingCharacterId(null);
    };

    setPlayingCharacterId(character.id);
  }, [playingCharacterId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="voice-selector-loading">
        <div className="spinner"></div>
        <p>{t('creator:workflow.voiceCharacter.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="voice-selector-error">
        <p>{t('creator:workflow.voiceCharacter.error', { message: error })}</p>
        <button onClick={() => window.location.reload()}>{t('creator:workflow.voiceCharacter.retry')}</button>
      </div>
    );
  }

  const currentCharacters = activeGender === 'Male' ? characters.male : characters.female;

  return (
    <div className="voice-character-selector">
      {speaker && (
        <div className="speaker-header">
          <h4>{t('creator:workflow.voiceCharacter.selectVoiceFor', { speaker })}</h4>
        </div>
      )}

      {/* Gender Tabs */}
      <div className="category-tabs">
        {GENDER_TABS.map((tab) => {
          const count = tab.id === 'Male' ? characters.male.length : characters.female.length;
          return (
            <button
              key={tab.id}
              className={`category-tab ${activeGender === tab.id ? 'active' : ''}`}
              onClick={() => setActiveGender(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              <span className="tab-count">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Character Grid */}
      <div className="characters-grid">
        {currentCharacters.map((character) => {
          const isSelected = character.id === selectedCharacterId;
          const isPlaying = character.id === playingCharacterId;

          return (
            <div
              key={character.id}
              className={`character-card ${isSelected ? 'selected' : ''}`}
              onClick={() => onSelectCharacter(character.id)}
            >
              <div className="character-header">
                <h5 className="character-name">{character.name}</h5>
                {isSelected && <span className="selected-badge">{t('creator:workflow.voiceCharacter.selected')}</span>}
              </div>

              <p className="character-characteristics">
                <span className="characteristics-label">{t('creator:workflow.voiceCharacter.style')}</span> {character.characteristics}
              </p>

              <div className="character-actions">
                <button
                  className={`preview-button ${isPlaying ? 'playing' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayPreview(character);
                  }}
                >
                  {isPlaying
                    ? t('creator:workflow.voiceCharacter.stop')
                    : t('creator:workflow.voiceCharacter.preview')}
                </button>
              </div>

              {/* Use Cases (collapsed by default) */}
              <details className="character-use-cases">
                <summary>{t('creator:workflow.voiceCharacter.useCases')}</summary>
                <ul>
                  {character.useCases.map((useCase, index) => (
                    <li key={index}>{useCase}</li>
                  ))}
                </ul>
              </details>
            </div>
          );
        })}
      </div>

      {currentCharacters.length === 0 && (
        <div className="no-characters">
          <p>{t('creator:workflow.voiceCharacter.noCharacters')}</p>
        </div>
      )}
    </div>
  );
};

export default VoiceCharacterSelector;
